import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, Injector, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  UserCredentials,
  UserRegistrationData,
} from '@orderna/admin-panel/src/app/model/user';
import { environment } from '@orderna/admin-panel/src/environments/environment';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { CurrentUser } from '../../model/currrent-user';
import { SuccessResponse } from '../../services/shared/success-response';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { AuthApiMapper } from './auth-api.mapper';
import { Role } from '../../model/enum/role';
import { getDefaultLandingPageByRole } from '@orderna/admin-panel/src/utils/get-default-landing-page';
import { StockLayerGatewayService } from '../../services/stock-layer-gateway/stock-layer-gateway.service';
import { NotificationGatewayService } from '../../services/notifications-gateway/notification-gateway.service';
import { AuthTokenPayload } from './auth.token-payload';
import { jwtDecode } from 'jwt-decode';

let API_URL = `${environment.api}/auth`;

@Injectable({ providedIn: 'root' })
export class AuthApiService {
  private currentUserSubject = new BehaviorSubject<CurrentUser | null>(null);

  currentUser$ = this.currentUserSubject.asObservable();

  currentUser = toSignal(this.currentUser$);

  #tokenPayload = signal<AuthTokenPayload | null>(null);

  // TODO: Extract current user service
  currentUserId = computed(() => this.#tokenPayload()?.userId);
  currentUserId$ = toObservable(this.currentUserId);
  currentUserTimezone = computed(() => this.#tokenPayload()?.timezone)
  currentUserRole = computed(() => this.#tokenPayload()?.role);

  isCurrentUserAdmin = computed(() => this.currentUserRole() === Role.ADMIN);

  tokenIat = computed(() => this.#tokenPayload()?.iat)

  #router = inject(Router);
  #localStorage = inject(LocalStorageService);
  #http = inject(HttpClient);
  #stockLayerGatewayService = inject(StockLayerGatewayService);
  #notificationGatewayService = inject(NotificationGatewayService);
  #injector = inject(Injector);

  getNewAccessToken(): Observable<string> {
    return this.#http.post<SuccessResponse>(`${API_URL}/token`, {}).pipe(
      map((response) => response.data.accessToken),
      tap(accessToken => {
        this.persistAuthTokens({ accessToken });
        this.#stockLayerGatewayService.refreshConnection();
        this.#notificationGatewayService.refreshConnection();
        this.syncPendingOfflineData();
      }),
    );
  }

  login(credentials: UserCredentials) {
    return this.#http
      .post<SuccessResponse>(API_URL + '/login', credentials)
      .pipe(
        map((response) => response.data),
        tap((data) => {
          const { accessToken, refreshToken } = data;
          this.persistAuthTokens({ accessToken, refreshToken });

          const isOnboarded = this.#localStorage.getIsOnboarded();
          const role = this.#localStorage.getRole()!;
          this.syncPendingOfflineData();

          if (isOnboarded) {
            this.goToDefaultLandingPage(role);
          } else {
            this.goToOnboardingPage();
          }
        }),
      );
  }

  /**
   * Sync pending offline data after login
   * This handles the case where sync failed due to 401 and user re-logged in
   * Uses lazy injection to avoid circular dependency
   */
  private async syncPendingOfflineData(): Promise<void> {
    try {
      const { RegisterOfflineWrapperService } =
        await import('../../services/shared/offline/register-offline-wrapper.service');
      const registerOfflineWrapperService = this.#injector.get(
        RegisterOfflineWrapperService,
      );

      const hasPendingData =
        await registerOfflineWrapperService.hasPendingOfflineData();
      if (hasPendingData) {
        await registerOfflineWrapperService.syncAllPendingOfflineData();
      }
    } catch (error) {
      console.error('Failed to sync pending offline data:', error);
    }
  }

  register(user: UserRegistrationData): Observable<any> {
    return this.#http
      .post<SuccessResponse>(
        `${API_URL}/signup`,
        AuthApiMapper.toSignupUserDto(user),
      )
      .pipe(
        map((response) => response.data),
        tap((response) => {
          // Check if email verification is required
          const { accessToken, refreshToken, requiresEmailVerification } =
            response;

          // Only persist tokens and redirect if email verification is NOT required
          // If verification IS required, user must verify email before accessing the app
          if (!requiresEmailVerification) {
            this.persistAuthTokens({ accessToken, refreshToken });
            this.goToOnboardingPage();
          }
          // If requiresEmailVerification is true, the component will handle showing
          // the verification message without storing tokens or redirecting
        }),
      );
  }

  async init(): Promise<void> {
    this.loadTokenPayload();
    await this.loadCurrentUser();
  }

  async logOut(forced: boolean = false, isChangedPassword: boolean = false) {
    console.log('Logging out user, forced:', forced);
    try {
      if (!forced) {
        await firstValueFrom(this.#http.post(`${API_URL}/logout`, {}));
      }

      this.currentUserSubject.next(null);
      this.#tokenPayload.set(null);

      this.#localStorage.clearAppStorage();
      this.#stockLayerGatewayService.disconnect();
      this.#notificationGatewayService.disconnect();
      if (!isChangedPassword) {
        await this.#router.navigateByUrl('/login');
      }
    } catch (error) {
      console.error('Failed to log out user', error);
      throw error;
    }
  }

  resetPassword(userData: any): Observable<any> {
    const transformedData = AuthApiMapper.toResetPasswordDto(userData);
    return this.#http.post(
      `${API_URL}/reset-password`,
      JSON.stringify(transformedData),
    );
  }

  changePassword(token: string, userData: any): Observable<any> {
    const transformedData = AuthApiMapper.toChangePasswordDto(userData);
    return this.#http.patch(
      `${API_URL}/change-password`,
      JSON.stringify(transformedData),
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
  }

  verifyEmail(token: string): Observable<any> {
    return this.#http.post<SuccessResponse>(`${API_URL}/verify-email`, {
      token,
    });
  }

  resendVerificationEmail(email: string): Observable<any> {
    return this.#http.post<SuccessResponse>(`${API_URL}/resend-verification`, {
      email,
    });
  }

  private persistAuthTokens(authTokens: {
    accessToken: string;
    refreshToken?: string;
  }) {
    this.#localStorage.setAccessToken(authTokens.accessToken);

    if (authTokens.refreshToken) {
      this.#localStorage.setRefreshToken(authTokens.refreshToken);
    }

    this.loadTokenPayload();
  }

  private async goToDefaultLandingPage(role: Role) {
    const defaultLandingPage = getDefaultLandingPageByRole(role);
    await this.#router.navigateByUrl(defaultLandingPage);
  }

  private async goToOnboardingPage() {
    await this.#router.navigateByUrl('/onboarding');
  }

  private loadTokenPayload() {
    const accessToken = this.#localStorage.getAccessToken();

    if (!accessToken) {
      this.#tokenPayload.set(null);
      return;
    }

    const payload = jwtDecode<AuthTokenPayload>(accessToken);
    this.#tokenPayload.set(payload);
  }

  private async loadCurrentUser() {
    const cachedUser = this.currentUser();

    if (cachedUser) {
      return cachedUser;
    }

    try {
      const response = await firstValueFrom(
        this.#http.get<SuccessResponse>(`${API_URL}/whoami`),
      );
      const user = response.data;

      this.currentUserSubject.next(user);

      return user;
    } catch (error) {
      console.error('Failed to fetch current user', error);
      this.currentUserSubject.next(null);
      this.#localStorage.clearAppStorage();
      throw error;
    }
  }
}
